/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */

import { computed, inject, Injectable } from '@angular/core';
import { TravelersStore } from '../../core/stores/travelers.store';
import { TripDatesStore } from '../../pages/trip-details/when-are-you-travelling/store/trip-dates.store';
import { DestinationsStore } from '../../pages/trip-details/where-are-you-travelling/store/destinations.store';
import { EcommerceItem, EcommerceModel, ItemCategory } from '../models/ecommerce.model';
import { ProductsStore } from '../../pages/covers-page/store/products.store';
import { QuoteOverviewService } from './quote/quote-overview.service';

@Injectable({
  providedIn: 'root'
})
export class EcommerceService {
  private ecommerceItems: EcommerceItem[] = [];
  private ecommerceObject: EcommerceModel = { items: [] };

  private policyType = '';
  private destinationsSnapshot = '';
  private proposalSnapshot = computed(() => this.productsStore.selectedProduct());
  private pickedCovers = computed(() => this.quoteOverviewService.pickedCovers());

  private travelersStore = inject(TravelersStore);
  private destinationsStore = inject(DestinationsStore);
  private tripDatesStore = inject(TripDatesStore);
  private productsStore = inject(ProductsStore);
  private quoteOverviewService = inject(QuoteOverviewService);

  get policyComposition(): string {
    const adults = this.travelersStore.adultTravelers().filter((t) => t.age > 0).length;
    const dependants = this.travelersStore.childTravelers().filter((t) => t.age > 0).length;
    return adults ? `${adults}${dependants ? `+${dependants}` : ''}` : '';
  }

  public eCommerceInit(): EcommerceModel {
    this.ecommerceObject.items.push({
      item_id: 0,
      item_name: 'TCA Policy',
      currency: 'AUD',
      item_category: ItemCategory.Policy,
      affiliation: 'Leisure'
    });

    return this.ecommerceObject;
  }

  public getEcommerceItems(coverId?: number): EcommerceItem[] {
    this.ecommerceItems = [];
    this.policyType = this.destinationsStore.pickedDestinationsData().every((dest) => dest.IsDomestic)
      ? 'Domestic'
      : 'International';
    this.destinationsSnapshot = this.destinationsStore
      .pickedDestinationsData()
      .map((d) => d.title)
      .toString();

    if (!coverId) this.pushEcommercePolicyItem();
    const covers = coverId ? this.pickedCovers().filter((cover) => cover.id === coverId) : this.pickedCovers();

    covers.forEach((cover) => {
      this.ecommerceItems.push({
        item_id: cover.id,
        item_name: cover.name,
        price: cover.price / cover.quantity,
        currency: 'AUD',
        item_category: ItemCategory.Additional,
        policy_type: this.policyType,
        destination: this.destinationsSnapshot,
        policy_length: this.tripDatesStore.tripLength(),
        policy_composition: this.policyComposition,
        excess_amount: this.proposalSnapshot()?.excessLevels.find((item) => item.IsSelected)?.ExcessLevelValue,
        cover_limit: this.proposalSnapshot()?.insuredAmountData.find((item) => item.IsSelected)?.InsuredAmountValue,
        days_before_departure: this.getDaysBeforeDeparture(),
        month_of_travel: `${this.getStartMonth()} - ${this.getEndMonth()}`,
        quantity: cover.quantity
      });
    });

    return this.ecommerceItems;
  }

  public pushEcommercePolicyItem(): void {
    this.ecommerceItems.push({
      item_id: this.proposalSnapshot()?.policyGroupID || 0,
      item_name: this.proposalSnapshot()?.name || '',
      price: this.proposalSnapshot()?.basicPrice || 0,
      currency: 'AUD',
      item_category: ItemCategory.Policy,
      policy_type: this.policyType,
      destination: this.destinationsSnapshot,
      policy_length: this.tripDatesStore.tripLength(),
      policy_composition: this.policyComposition,
      days_before_departure: this.getDaysBeforeDeparture(),
      excess_amount: this.proposalSnapshot()?.excessLevels.find((item) => item.IsSelected)?.ExcessLevelValue,
      cover_limit: this.proposalSnapshot()?.insuredAmountData.find((item) => item.IsSelected)?.InsuredAmountValue,
      discount: this.proposalSnapshot()?.discountPercentage,
      quantity: 1,
      month_of_travel: `${this.getStartMonth()} - ${this.getEndMonth()}`
    });
  }

  private getDaysBeforeDeparture(): number {
    const today = new Date();
    const startDate = Number(this.tripDatesStore.dates()?.start);
    const differenceInMilliseconds = startDate - today.getTime();
    return Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24) + 1);
  }

  private getStartMonth(): string | undefined {
    return this.tripDatesStore.dates()?.start?.toLocaleDateString('en-US', { month: 'long' });
  }

  private getEndMonth(): string | undefined {
    return this.tripDatesStore.dates()?.end?.toLocaleDateString('en-US', { month: 'long' });
  }
}
